import { Box, Typography } from '@mui/material'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import React, { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getLeaveRequestById, postClaimRequest, putLeaveRequest } from 'services/api/dashboard'
import defaultAvatar from 'assets/images/default_avatar.png'
import RequestApproval from 'module/leave/pages/requestDetails/components/requestApproval'
import { getSingleApprovalReport } from 'services/api/modules/claim/approval-center/report'
import { format } from 'date-fns'
import { parseISO } from 'date-fns'
import AdxFileAttachmentLeave from 'components/reusable/fileAttachment/AdxFileAttachmentLeave'
import ActionConfirmationModal from 'pages/approval-center/components/ActionConfirmationModal'
import ClaimProgressStepper from 'module/claim/components/ClaimProgressStepper'
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik'
import * as yup from 'yup'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import ExpenseListPayable from 'pages/approval-center/components/ExpenseListPayable'
import { renderCountBadge } from 'components/custom/AdxLabelRenderer'
import CreditApproval from 'module/leave/pages/leaveCreditDetails/components/creditApproval'

const ToApproveModal = ({
  isOpen,
  handleClose,
  approvalId,
  category,
  leaveRequestId,
  leaveType,
}) => {
  // const { data: userData } = useContext(authContext).auth
  const isLeave = category === 'leave'
  const isLeaveCredit = category === 'leave' && leaveType === 2
  const isClaim = category === 'claim'

  const queryClient = useQueryClient()

  const { data: leaveRequestById, isLoading: leaveRequestIsLoading } = useQuery(
    ['getLeaveRequestById', leaveRequestId],
    async () => {
      if (!leaveRequestId) return null
      if (isLeave) {
        const { data } = await getLeaveRequestById(leaveRequestId)

        return data[0]
      }
    },
    {
      enabled: !!approvalId && isLeave,
      refetchOnMount: true,
    }
  )
  const { data: claimRequestById, isLoading: claimRequestIsLoading } = useQuery(
    ['getClaimRequestById', approvalId],
    async () => {
      if (!approvalId) return null
      if (isClaim) {
        const { data } = await getSingleApprovalReport(approvalId)
        return data
      }
    },
    {
      enabled: !!approvalId && isClaim,
      refetchOnMount: true,
    }
  )

  const requestById = useMemo(() => {
    if (isLeave) return leaveRequestById
    if (isClaim) return claimRequestById
    return null
  }, [isLeave, isClaim, leaveRequestById, claimRequestById])

  const isLoading = isLeave ? leaveRequestIsLoading : isClaim ? claimRequestIsLoading : false

  // Approval Mutations
  const { mutate: mutateLeaveRequest, isLoading: mutateLeaveRequestIsLoading } = useMutation({
    mutationFn: async (payload) => {
      return await putLeaveRequest(approvalId, payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries('getDashboardApprovals')
      await queryClient.invalidateQueries('getLeaveRequestById')
      handleClose()
    },
    onError: (error) => {
      console.error(error)
    },
  })
  const { mutate: mutateClaimRequest, isLoading: mutateClaimRequestIsLoading } = useMutation({
    mutationFn: async (payload) => {
      return await postClaimRequest(approvalId, payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries('getDashboardApprovals')
      await queryClient.invalidateQueries('getClaimRequestById')
      handleClose()
    },
    onError: (error) => {
      console.error(error)
    },
  })

  const amountPayableRef = useRef()

  const [amount, setAmount] = useState(null)

  const newValues = amountPayableRef?.current?.values?.expenseArray.map((list) => ({
    id: list.id,
    amount: list.amount,
    amount_payable: list.amount_payable,
  }))

  const getMutationConfig = () => {
    if (isLeave || isLeaveCredit) {
      return {
        mutate: mutateLeaveRequest,
        isLoading: mutateLeaveRequestIsLoading,
        buildPayload: (status) => ({
          staff_id: requestById.staff_id,
          status: status,
        }),
      }
    }

    if (isClaim) {
      return {
        mutate: mutateClaimRequest,
        isLoading: mutateClaimRequestIsLoading,
        buildPayload: (status) => ({
          action: status,
          expenses: newValues,
        }),
      }
    }

    throw new Error('Invalid request type')
  }

  const mutationConfig = getMutationConfig()

  const mappedDetails = useMemo(() => {
    if (!requestById) return []

    if (isLeave) {
      return [
        { label: 'Type', value: requestById?.leave_policy_name },
        { label: 'Shift', value: requestById?.type },
        {
          label: 'Date',
          value: (
            <>
              <Typography as="span">
                {requestById?.start_date &&
                  `${format(parseISO(requestById?.start_date), `eee, dd/MM/yyyy`)} - ${format(
                    parseISO(requestById?.end_date),
                    `eee, dd/MM/yyyy`
                  )}`}
              </Typography>
              <Typography as="span" variant="body1" sx={{ marginLeft: 1, color: 'alternate.main' }}>
                ({requestById?.duration} {requestById?.duration === 1 ? 'day' : 'days'})
              </Typography>
            </>
          ),
        },
        { label: 'Requester', value: requestById?.staff_name },
      ]
    }
    if (isClaim) {
      return [
        { label: 'Title', value: requestById?.title },
        { label: 'Type', value: requestById?.claim_report_type },
        {
          label: 'Claim Date',
          value:
            requestById?.start_date && requestById?.end_date
              ? `${requestById.start_date} - ${requestById.end_date}`
              : 'N/A',
        },
        { label: 'Requester', value: requestById?.staff_name },
      ]
    }
    return []
  }, [requestById, isLeave, isClaim])

  const validationSchema = yup.object().shape({
    expenseArray: yup.array().of(
      yup.object().shape({
        amount_payable: yup
          .number()
          .typeError('Invalid amount')
          .positive('Invalid amount')
          .min(0.01, 'Invalid amount')
          .required('Amount is required'),
      })
    ),
  })

  const headerAvatar = (mobile) => (
    <Box
      as="img"
      sx={{
        display: mobile ? { xs: 'block', lg: 'none' } : { xs: 'none', lg: 'block' },
        height: 50,
        width: 50,
        bgcolor: 'gray-background.main',
        objectFit: 'cover',
        borderRadius: '50%',
        border: '3px solid',
        borderColor: 'white.main',
      }}
      alt={'Requester avatar'}
      src={isLeave ? requestById?.staff_avatar : isClaim ? requestById?.staff_img : defaultAvatar}
    />
  )

  const customHeader = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingX: '24px',
        paddingY: '15px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {headerAvatar(false)}
        <Typography variant="subtitle1">
          {isLeave ? requestById?.type : isClaim ? requestById?.title : ''}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: 'alternate.main' }}>
          •
        </Typography>
        {isLeave ? (
          <Typography variant="subtitle1" sx={{ color: 'alternate.main' }}>{`${
            requestById?.duration
          } day${requestById?.duration > 1 ? 's' : ''}`}</Typography>
        ) : isClaim ? (
          <Typography variant="subtitle1" sx={{ color: 'alternate.main' }}>
            RM{requestById?.amount_total}
          </Typography>
        ) : null}
      </Box>
      <Box
        onClick={handleClose}
        sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="21px" height="21px" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M18.3 5.71a.996.996 0 0 0-1.41 0L12 10.59L7.11 5.7A.996.996 0 1 0 5.7 7.11L10.59 12L5.7 16.89a.996.996 0 1 0 1.41 1.41L12 13.41l4.89 4.89a.996.996 0 1 0 1.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4"
          />
        </svg>
      </Box>
    </Box>
  )

  const customFooter = (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'primary.light',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '10px',
        padding: '20px',
        borderRadius: '0 0 10px 10px',
      }}
    >
      {isClaim && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          <Typography variant="h4">Total reimbursement</Typography>
          <Typography variant="h4">{amount}</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px' }}>
        <ActionConfirmationModal
          actionType="REJECT"
          btnText="Reject"
          actionFn={() => mutationConfig.mutate(mutationConfig.buildPayload(0))}
          mutation={mutationConfig}
          sxButton={{ width: '150px' }}
        />
        <ActionConfirmationModal
          actionType="APPROVE"
          btnText="Approve"
          actionFn={() => mutationConfig.mutate(mutationConfig.buildPayload(1))}
          mutation={mutationConfig}
          sxButton={{ width: '150px' }}
        />
      </Box>
    </Box>
  )

  if (isLoading) return null

  return (
    <AdxFormModal
      customHeader={customHeader}
      open={isOpen}
      handleClose={handleClose}
      disableAutoFocus
      sxModal={{ borderRadius: '10px' }}
      enableFooter
      footerprops={customFooter}
    >
      <Box
        sx={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '30px',
          marginX: 'auto',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
          {mappedDetails?.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', gap: '20px' }}>
              <Typography
                variant="body1"
                sx={{ color: 'text.quaternary', width: '120px', flexShrink: 0 }}
              >
                {item.label}
              </Typography>
              <Typography>{item.value}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ width: '100%' }}>
          <Typography variant="body1" sx={{ color: 'text.quaternary' }}>
            {isLeave ? 'Leave purpose' : isClaim ? 'Claim purpose' : ''}
          </Typography>
          <Typography sx={{ marginTop: '5px' }}>
            {isLeave ? requestById?.reason : isClaim ? requestById?.description : 'N/A'}
          </Typography>
        </Box>
        {isLeave && requestById?.attachment && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="body1" sx={{ color: 'text.quaternary' }}>
              Supporting document
            </Typography>
            <AdxFileAttachmentLeave
              item={{
                name: requestById?.attachment,
                url: requestById?.attachment_url,
              }}
            />
          </Box>
        )}
        {isClaim && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ color: 'text.quaternary' }}>
                Expenses
              </Typography>
              {renderCountBadge(requestById?.expenses?.length)}
            </Box>
            <Formik
              innerRef={amountPayableRef}
              enableReinitialize
              initialValues={{
                expenseArray: requestById?.expenses
                  ? requestById?.expenses?.map((item) => ({
                      id: item.id,
                      category: item?.expense_category?.name,
                      date: item.date,
                      merchant: item.merchant,
                      description: item.description,
                      amount: parseFloat(item.amount_payable || item.amount).toFixed(2),
                      amount_payable: parseFloat(
                        item.amount_payable === 0 ? item.amount : item.amount_payable
                      ).toFixed(2),
                      file: {
                        name: item?.attachment,
                        url: item?.attachment_url,
                      },
                    }))
                  : [{ id: '', amount: '', amount_payable: '' }],
              }}
              validationSchema={validationSchema}
            >
              {({ errors, touched, values, handleChange, handleBlur, setFieldValue, isValid }) => {
                const totalAmountPayable = `RM ${parseFloat(
                  values.expenseArray
                    .map((list) => parseFloat(list.amount_payable))
                    .reduce((a, b) => a + b)
                ).toFixed(2)}`

                setAmount(totalAmountPayable === 'RM NaN' ? 'RM 0.00' : totalAmountPayable)

                return (
                  <Form>
                    <FieldArray
                      name="expenseArray"
                      render={() =>
                        values.expenseArray.map((item, index) => (
                          <Field
                            name={`expenseArray.${index}.amount_payable`}
                            as={ExpenseListPayable}
                            onChange={handleChange}
                            onBlur={(e) => {
                              const rawValue = parseFloat(e.target.value)

                              if (isNaN(rawValue) || rawValue <= 0) {
                                setFieldValue(`expenseArray.${index}.amount_payable`, '')
                                return
                              }

                              setFieldValue(
                                `expenseArray.${index}.amount_payable`,
                                rawValue.toFixed(2)
                              )
                            }}
                            editable={true}
                            key={index}
                            id={item.id}
                            reportId={requestById?.id}
                            category={item.category}
                            date={item.date}
                            merchant={item.merchant}
                            description={item.description}
                            amount={item.amount}
                            file={item?.file}
                            value={values.expenseArray[index].amount_payable}
                            errorMessage={
                              <ErrorMessage
                                name={`expenseArray.${index}.amount_payable`}
                                component={AdxErrorMessage}
                              />
                            }
                            isErrorShown={
                              errors?.expenseArray?.length &&
                              errors?.expenseArray[index]?.amount_payable
                            }
                          />
                        ))
                      }
                    />
                  </Form>
                )
              }}
            </Formik>
          </Box>
        )}
        <Box sx={{ width: '100%' }}>
          {isLeave &&
            (isLeaveCredit ? (
              <CreditApproval
                approvalArr={requestById?.leave_approval || []}
                requestItem={requestById}
              />
            ) : (
              <RequestApproval
                approvalArr={requestById?.leave_approval || []}
                requestItem={requestById}
              />
            ))}
          {isClaim && <ClaimProgressStepper reportData={requestById} />}
        </Box>
      </Box>
    </AdxFormModal>
  )
}

export default ToApproveModal
